import { useRef, useState } from "react";
import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import {
  onSaveStatFile,
  UploadBatchReceipt,
} from "./actionHandlers/onSaveStatFile";
import { useStatTemplateDownload } from "./hooks/useStatTemplateDownload";

interface StatFileUploadModalProps {
  onClose: () => void;
}

/** Uploads one CSV batch and reports acceptance separately from background completion. */
const StatFileUploadModal = ({ onClose }: StatFileUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [nonEpaRequired, setNonEpaRequired] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState<UploadBatchReceipt | null>(null);
  const submitting = useRef(false);
  const { downloadTemplate, isDownloading } = useStatTemplateDownload();

  const handleSave = async () => {
    if (receipt) {
      onClose();
      return;
    }
    if (!file || submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    setError("");
    try {
      setReceipt(await onSaveStatFile(file, nonEpaRequired));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Upload failed.");
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      dialogId={ModalId.STAT_FILE_UPLOAD_MODAL}
      title="Upload & run PA"
      onClose={() => {
        if (!submitting.current) onClose();
      }}
      onSave={handleSave}
      saveButtonText={
        receipt ? "Done" : isSubmitting ? "Uploading…" : "Upload & run PA"
      }
      cancelText="Cancel"
      disableSave={isSubmitting || (!file && !receipt)}
    >
      <div className="flex flex-col gap-4">
        {receipt ? (
          <div
            role="status"
            className="rounded border border-green-300 bg-green-50 p-4"
          >
            <p className="font-semibold">
              {receipt.row_count} rows imported. PA pipeline queued.
            </p>
            <p>{receipt.filename}</p>
            <p>
              Refresh Delivery to see imported rows. Worklist records appear as
              OncoEMR and CMM processing progresses.
            </p>
          </div>
        ) : (
          <>
            <p>
              Upload a CSV, import its rows, then run OncoEMR and CMM processing.
            </p>
            <p className="text-sm">
              Use NYCBS_TYPE_RisaHealth_YYYYMMDDHHMM.csv so Batch and DOW can be
              derived.
            </p>
            <button
              type="button"
              className="text-blue-700 underline"
              onClick={downloadTemplate}
              disabled={isDownloading || isSubmitting}
            >
              {isDownloading ? "Downloading…" : "Download CSV template"}
            </button>
            <label className="flex flex-col gap-2">
              <span className="font-semibold">CSV file (up to 10 MB)</span>
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={isSubmitting}
                onChange={(event) => {
                  setError("");
                  const selected = event.target.files?.[0] ?? null;
                  if (
                    selected &&
                    (!selected.name.toLowerCase().endsWith(".csv") ||
                      selected.size > 10 * 1024 * 1024)
                  ) {
                    setFile(null);
                    setError("Choose a CSV file no larger than 10 MB.");
                    return;
                  }
                  setFile(selected);
                }}
              />
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={nonEpaRequired}
                disabled={isSubmitting}
                onChange={(event) => setNonEpaRequired(event.target.checked)}
              />
              Mark this batch as requiring non-ePA
            </label>
            {isSubmitting && (
              <p role="status">
                Uploading and importing rows. Keep this window open.
              </p>
            )}
            {error && (
              <p role="alert" className="text-red-700">
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
export default StatFileUploadModal;
